import { Show, Hide, Flex, Image, Text, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import type { AddressFromToFilter } from 'types/api/address';
import type { Transaction, TransactionsSortingField, TransactionsSortingValue } from 'types/api/transaction';

import useIsMobile from 'lib/hooks/useIsMobile';
import { useI18n } from 'lib/i18n/context';
import { ACTION_BAR_HEIGHT_DESKTOP } from 'ui/shared/ActionBar';
import DataListDisplay from 'ui/shared/DataListDisplay';
import type { QueryWithPagesResult } from 'ui/shared/pagination/useQueryWithPages';
import getNextSortValue from 'ui/shared/sort/getNextSortValue';

import useDescribeTxs from './noves/useDescribeTxs';
import TransactionsExportButton from './TransactionsExportButton';
import TxsExplorerDesktop from './TxsExplorerDesktop';
import TxsHeaderMobile from './TxsHeaderMobile';
import TxsList from './TxsList';
import TxsTable from './TxsTable';

const SORT_SEQUENCE: Record<TransactionsSortingField, Array<TransactionsSortingValue | undefined>> = {
  value: [ 'value-desc', 'value-asc', undefined ],
  fee: [ 'fee-desc', 'fee-asc', undefined ],
  block_number: [ 'block_number-asc', undefined ],
};

type Props = {
  // eslint-disable-next-line max-len
  query: QueryWithPagesResult<'txs_validated' | 'txs_pending' | 'txs_with_blobs'> | QueryWithPagesResult<'txs_watchlist'> | QueryWithPagesResult<'block_txs'> | QueryWithPagesResult<'zkevm_l2_txn_batch_txs'>;
  showBlockInfo?: boolean;
  showSocketInfo?: boolean;
  socketInfoAlert?: string;
  socketInfoNum?: number;
  currentAddress?: string;
  filter?: React.ReactNode;
  filterValue?: AddressFromToFilter;
  enableTimeIncrement?: boolean;
  top?: number;
  stickySurface?: boolean;
  items?: Array<Transaction>;
  isPlaceholderData: boolean;
  isError: boolean;
  setSorting: (value: TransactionsSortingValue | undefined) => void;
  sort: TransactionsSortingValue | undefined;
  mobileExportButtonPosition?: 'inline' | 'belowPagination';
  variant?: 'default' | 'explorer';
  desktopTopSlot?: React.ReactNode;
  hideValueUsd?: boolean;
};

const TxsContent = ({
  query,
  filter,
  filterValue,
  showBlockInfo = true,
  showSocketInfo = true,
  socketInfoAlert,
  socketInfoNum,
  currentAddress,
  enableTimeIncrement,
  top,
  stickySurface,
  items,
  isPlaceholderData,
  isError,
  setSorting,
  sort,
  mobileExportButtonPosition = 'inline',
  variant = 'default',
  desktopTopSlot,
  hideValueUsd,
}: Props) => {
  const isMobile = useIsMobile();
  const { t } = useI18n();

  const onSortToggle = React.useCallback((field: TransactionsSortingField) => () => {
    const value = getNextSortValue<TransactionsSortingField, TransactionsSortingValue>(SORT_SEQUENCE, field)(sort);
    setSorting(value);
  }, [ sort, setSorting ]);

  const noDataIcon = useColorModeValue('/static/transactions/tx-no-data-light.svg', '/static/transactions/tx-no-data.svg');

  const emptyContent = (
    <Flex direction="column" alignItems="center" gap="16px" py="120px">
      <Image src={ noDataIcon } alt="" width="70px" height="70px"/>
      <Text
        fontFamily="'Space Grotesk', sans-serif"
        fontWeight={ 500 }
        fontSize="18px"
        lineHeight="20px"
        color="#616161"
      >
        { t('txs.noDataYet') }
      </Text>
    </Flex>
  );

  const itemsWithTranslation = useDescribeTxs(items, currentAddress, query.isPlaceholderData);

  const content = itemsWithTranslation ? (
    <>
      <Show below="lg" ssr={ false }>
        <TxsList
          showBlockInfo={ showBlockInfo }
          showSocketInfo={ showSocketInfo }
          socketInfoAlert={ socketInfoAlert }
          socketInfoNum={ socketInfoNum }
          isLoading={ isPlaceholderData }
          enableTimeIncrement={ enableTimeIncrement }
          currentAddress={ currentAddress }
          items={ itemsWithTranslation }
          hideValueUsd={ hideValueUsd }
        />
      </Show>
      <Hide below="lg" ssr={ false }>
        { variant === 'explorer' ? (
          <TxsExplorerDesktop
            txs={ itemsWithTranslation }
            showBlockInfo={ showBlockInfo }
            showSocketInfo={ showSocketInfo }
            socketInfoAlert={ socketInfoAlert }
            socketInfoNum={ socketInfoNum }
            top={ top ?? (query.pagination.isVisible ? ACTION_BAR_HEIGHT_DESKTOP : 0) }
            currentAddress={ currentAddress }
            enableTimeIncrement={ enableTimeIncrement }
            isLoading={ isPlaceholderData }
            desktopTopSlot={ desktopTopSlot }
            hideValueUsd={ hideValueUsd }
          />
        ) : (
          <TxsTable
            txs={ itemsWithTranslation }
            sort={ onSortToggle }
            sorting={ sort }
            showBlockInfo={ showBlockInfo }
            showSocketInfo={ showSocketInfo }
            socketInfoAlert={ socketInfoAlert }
            socketInfoNum={ socketInfoNum }
            top={ top || (query.pagination.isVisible ? ACTION_BAR_HEIGHT_DESKTOP : 0) }
            stickySurface={ stickySurface }
            currentAddress={ currentAddress }
            enableTimeIncrement={ enableTimeIncrement }
            isLoading={ isPlaceholderData }
            variant={ variant }
            desktopTopSlot={ desktopTopSlot }
          />
        ) }
      </Hide>
    </>
  ) : null;

  const actionBar = isMobile ? (
    <TxsHeaderMobile
      mt="16px"
      paginationProps={ query.pagination }
      showPagination={ query.pagination.isVisible }
      filterComponent={ filter }
      linkSlot={ currentAddress ? (
        <TransactionsExportButton
          address={ currentAddress }
          filterValue={ filterValue }
          isLoading={ query.pagination.isLoading }
          items={ items }
        />
      ) : null
      }
      linkSlotPosition={ mobileExportButtonPosition }
    />
  ) : null;

  if (variant === 'explorer') {
    const explorerContent = (
      <TxsExplorerDesktop
        txs={ itemsWithTranslation || [] }
        showBlockInfo={ showBlockInfo }
        showSocketInfo={ showSocketInfo }
        socketInfoAlert={ socketInfoAlert }
        socketInfoNum={ socketInfoNum }
        top={ isMobile ? 64 : (top ?? (query.pagination.isVisible ? ACTION_BAR_HEIGHT_DESKTOP : 0)) }
        currentAddress={ currentAddress }
        enableTimeIncrement={ enableTimeIncrement }
        isLoading={ isPlaceholderData }
        desktopTopSlot={ desktopTopSlot }
        hideValueUsd={ hideValueUsd }
        noBorder={ isMobile }
      />
    );

    return isError ? emptyContent : explorerContent;
  }

  return (
    <DataListDisplay
      isError={ isError }
      items={ itemsWithTranslation }
      emptyText={ emptyContent }
      content={ content }
      actionBar={ actionBar }
    />
  );
};

export default TxsContent;
